/**
 * UnsubscribePage - Public Token-Based Unsubscribe
 * Feature 3: Email Intelligence Platform — Phase 2
 * MDM-EMAIL-P2-001 / EMAIL-021
 *
 * Public page accessible via unsubscribe token URL without authentication.
 * Allows users to unsubscribe from specific categories or all marketing emails.
 */
import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { emailPreferencesAPI } from '../../services/api';

const UNSUBSCRIBE_OPTIONS = [
  { value: 'all', label: 'Unsubscribe from all marketing emails' },
  { value: 'marketing', label: 'Unsubscribe from marketing only' },
  { value: 'gap_alerts', label: 'Unsubscribe from gap alerts only' },
  { value: 'digest', label: 'Unsubscribe from digest only' },
  { value: 'convention_alerts', label: 'Unsubscribe from convention alerts only' },
];

export default function UnsubscribePage({ token, onComplete }) {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);
  const [unsubscribeType, setUnsubscribeType] = useState('all');
  const [reason, setReason] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await emailPreferencesAPI.getUnsubscribePage(token);
        if (!cancelled) setPageData(data);
      } catch (err) {
        if (!cancelled) setError('This unsubscribe link is invalid or has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await emailPreferencesAPI.submitUnsubscribe(token, {
        unsubscribe_type: unsubscribeType,
        reason: reason || null,
      });
      setConfirmation(result);
      setCompleted(true);
    } catch (err) {
      setError('Failed to process unsubscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  // Error state (invalid token)
  if (error && !pageData) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Invalid Link</h1>
          <p className="text-zinc-400 text-sm">{error}</p>
          <button
            onClick={onComplete}
            className="mt-6 text-orange-500 hover:text-orange-400 text-sm font-medium"
          >
            Return to MAESTRO
          </button>
        </div>
      </div>
    );
  }

  // Confirmation state
  if (completed && confirmation) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Successfully Unsubscribed</h1>
          <p className="text-zinc-400 text-sm">{confirmation.message}</p>
          <button
            onClick={onComplete}
            className="mt-6 text-orange-500 hover:text-orange-400 text-sm font-medium"
          >
            Return to MAESTRO
          </button>
        </div>
      </div>
    );
  }

  // Main unsubscribe form
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="font-maestro-mono text-2xl font-bold" style={{ letterSpacing: '0.15em' }}><span className="text-maestro-gold">M</span><span className="text-white">AESTRO</span></h1>
          <p className="text-zinc-400 text-sm mt-2">
            Managing preferences for: <span className="text-zinc-300">{pageData?.email_masked}</span>
          </p>
        </div>

        <h2 className="text-lg font-semibold text-white mb-4">Unsubscribe</h2>

        <div className="space-y-2 mb-6">
          {UNSUBSCRIBE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-3 p-3 bg-zinc-800/50 border border-zinc-700/50 rounded-lg cursor-pointer hover:border-zinc-600 transition-colors"
            >
              <input
                type="radio"
                name="unsubscribe_type"
                value={option.value}
                checked={unsubscribeType === option.value}
                onChange={(e) => setUnsubscribeType(e.target.value)}
                className="text-orange-500 focus:ring-orange-500 focus:ring-offset-zinc-900"
              />
              <span className="text-sm text-zinc-300">{option.label}</span>
            </label>
          ))}
        </div>

        <div className="mb-6">
          <label className="block text-sm text-zinc-400 mb-1">Tell us why (optional)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Your feedback helps us improve..."
            className="bg-zinc-800 border border-zinc-700 text-white rounded-lg w-full p-3 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 resize-none"
          />
          <p className="text-xs text-zinc-600 mt-1">{reason.length}/500</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-6 py-2 rounded-lg transition-colors"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Unsubscribe
        </button>

        <p className="text-xs text-zinc-600 text-center mt-4">
          You will still receive transactional emails (order confirmations, password resets).
        </p>
      </div>
    </div>
  );
}
